import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import styled from "@emotion/styled";
import { CircularProgress, TextField as MuiTextField } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { fetchPagedPropertiesWithLimit } from "../../../services/propertyService";
import { useSelector } from "../../../redux/store";
import debounce from "lodash.debounce";
import {getTextByCurrentLanguage} from "../../../utils/functions";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const TextField = styled(MuiTextField)`
  & label {
    color: #FFFFFF;
  }
  & .MuiOutlinedInput-root {
    padding: 0;
    padding-left: 7px;
    border-radius: 8px;
    background: #233145;
    color: #FFFFFF;
    & input::placeholder {
      font-size: 14px
    }
    & fieldset {
      border-color: rgba(255, 255, 255, 0.23);
    }
    & label {
      color: #FFFFFF
    }
    &:hover fieldset {
      border-color: white;
    }
  }
  & .MuiChip-label {
    color: #FFFFFF
  }
  & .MuiAutocomplete-tag {
    background: #ffffff24;
  }
  & .MuiChip-deleteIconMedium {
    color: #ffffff7d !important;
  }
  & .MuiAutocomplete-popupIndicator {
    color: #FFFFFF
  }
  & .MuiAutocomplete-clearIndicator {
    color: #ffffff7d
  }
 `

type PropsType = {
  selectedItemsToSearch: (selectedItems: Array<any>) => void
}

const CustomSearch: FC<PropsType> = ({selectedItemsToSearch}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>([]);
  const [selectedValues, setSelectedValues] = useState<any>([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)
  const user = useSelector((state) => state.authReducer.user);

  const getOptionsDelayed = useCallback(
    debounce((text, callback) => {
      setLoading(true)
      setOptions([]);
      fetchPagedPropertiesWithLimit(user!.token, 1, 9000, text)
        .then(callback)
    }, 500),
    []);

  useEffect(() => {
    if (searchText.length >= 3) {
      getOptionsDelayed(searchText, (response: any) => {
        setOptions(response.data.results);
        setLoading(false)
      });
    } else {
      setOptions([])
    }
  }, [searchText, getOptionsDelayed]);

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      disableCloseOnSelect
      onChange={(e, value) => (
        setSelectedValues(value)
      )}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'clear') {
          selectedItemsToSearch([])
        }
      }}
      getOptionLabel={(option: any) => getTextByCurrentLanguage(option.name)}
      isOptionEqualToValue={(option: any, value) => option.id === value.id}
      open={open}
      loading={loading}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {getTextByCurrentLanguage(option.name)}
        </li>
      )}
      style={{ width: 397 }}
      renderInput={(params) => (
        <TextField
          {...params}
          value={searchText}
          onChange={(e) => (
            setSearchText(e.target.value)
          )}
          inputProps={{
            ...params.inputProps,
            onKeyDown: (e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                setOpen(false)
                selectedItemsToSearch(selectedValues)
              }
            },
          }}
          InputLabelProps={{shrink: false}}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          placeholder="Search"
        />
      )}
    />
  );
}

export default CustomSearch
